import _ from 'lodash';
import { history } from 'umi';

import { bindServices } from '@/services/api';
import { updateFavorite } from './service';

const api = bindServices('entities');
import request from '@/utils/infrastructure/communication/request';
import { toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { filtersConfig } from './ManageEntities/utils';
import { groupsFiltersConfig } from './manage-groups/utils';

const meta = { page: 1, limit: 5, count: 0, totalPages: 0 };

const initialState = {
  data: [],
  groups: [],
  premium: [],
  favorites: [],
  groupsPagination: meta,
  premiumPagination: meta,
  pagination: meta,
  favoritesPagination: meta,
  orderBy: '',
  searchCriteria: '',
  filtersConfig,
  groupsFiltersConfig,
  favoritesChanged: false,
  // Config-form contract (useConfigForm / ConfigFormDrawer)
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
};

const Model = {
  namespace: 'entities',
  state: initialState,
  effects: {
    *fetchGroups({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/groups',
          ...CoreQueries.ManageEntities.Groups,
          ...payload,
        });
        yield put({
          type: 'queryGroups',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    // Main management view (`/entities` sidebar menu) → `/entity/manage`.
    // Per the UX guide `/manage` is reserved for top-level management
    // screens accessed from the left sidebar; every other context
    // (selectors, dependencies, pickers, embedded dialogs) uses
    // `/entity/allowed`. `canManage` gates configuration UI;
    // `isFavorite` drives the star icon on each card.
    *fetchList({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/manage',
          ...CoreQueries.ManageEntities.Entities,
          select: `${CoreQueries.ManageEntities.Entities.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryEntities',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    *fetchPremiumList({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'entity/manage',
          ...CoreQueries.ManageEntities.PremiumEntities,
          select: `${CoreQueries.ManageEntities.PremiumEntities.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryPremiumEntities',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    // Favorites-first load via the dedicated `/entity/favorites`
    // endpoint. `canManage` is required for the row-command kebab and
    // `isFavorite` keeps the star icon filled (the endpoint returns
    // only favorites but the flag isn't auto-included in the select).
    *fetchFavorites({ payload }, { call, put }) {
      try {
        // Backend defaults to 5 items when $top/$skip are omitted.
        const res = yield call(api.getAll, {
          route: 'entity/favorites',
          page: 1,
          limit: 500,
          ...CoreQueries.ManageEntities.Favorites,
          select: `${CoreQueries.ManageEntities.Favorites.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryFavorites',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    // Star toggle — mirrors the `/processes` contract:
    //   1. `querySetFavorite` flips the flag locally (optimistic).
    //   2. `markFavoritesChanged` flags the slice so the next time the
    //      user lands on the favorites tab, `onTabChange` refreshes it.
    // No re-fetch happens here: re-reading `/entity/manage` or
    // `/entity/favorites` right after the POST/DELETE clobbers the
    // optimistic update with potentially-stale replica data.
    *setFavorite({ payload }, { call, put }) {
      try {
        yield call(updateFavorite, payload);
        yield put({ type: 'querySetFavorite', payload });
        yield put({ type: 'markFavoritesChanged' });
      } catch (e) {}
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    // Only refresh the favorites tab when the slice has been touched
    // since the last fetch (`favoritesChanged`). Same contract as
    // `/processes`: a clean re-entry keeps the cached list intact.
    *onTabChange({ payload }, { put, select }) {
      if (payload === 'favorites') {
        const { favoritesChanged } = yield select((state) => state.entities);
        if (favoritesChanged) {
          yield put({ type: 'fetchFavorites', payload: { page: 1 } });
          yield put({ type: 'clearFavoritesChanged' });
        }
      }
    },

    *toggleActive({ payload }, { call, put }) {
      try {
        yield call(request.post, `/dynamicEntity/activate/${payload.id}/${payload.isActive}`, {});
        yield put({
          type: 'queryToggleActive',
          payload: { id: payload.id, isActive: payload.isActive },
        });
      } catch (e) {}
    },

    // Generic delete used by the Launchpad EntityDeleteHost.
    *deleteItem({ payload }, { call, put, select }) {
      try {
        yield call(api.apiRoute, {
          route: `dynamicEntity/${payload.id}`,
          method: 'delete',
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'queryDeleteItem', payload: { id: payload.id } });
        const { showForm, id } = yield select((state) => state.entities);
        if (showForm && id === payload.id) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, id: null, item: null, detail: false },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationGlobal',
            payload: { visible: true, type: 'error' },
          });
        }
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: 'dynamicEntity',
          id: payload.id,
          select:
            'id,displayName,displayPluralName,description,order,isActive,isPremiumEntity,isExternalViewEnabled,dynamicEntityGroupId,created,updated',
          expand: {
            dynamicEntityGroup: { select: 'id,name' },
            userCreator: { select: 'id,fullName' },
            userModifier: { select: 'id,fullName' },
          },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {}
    },

    *fetchCreate({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const result = yield call(api.create, {
          route: 'dynamicEntity',
          data: payload.data,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'success', payload: { success: true } });

        const newId = result?.id || result?.data?.id;
        if (newId) {
          history.push(`/entities/${newId}/builder`);
        }
      } catch (err) {
        if (err?.message) {
          const { message, validationErrors } = err;
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message },
            validationErrors,
          });
        }
      }
    },

    *fetchUpdate({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.update, {
          route: 'dynamicEntity',
          data: { ...payload.data, id: payload.data.id },
        });
        // Close drawer as soon as the API returns OK.
        yield put({
          type: 'queryShowForm',
          payload: { state: false, id: null, item: null, detail: false },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'success', payload: { success: true } });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
    },

    *cleanup(o, { put }) {
      yield put({ type: 'queryCleanup' });
    },
  },
  reducers: {
    queryGroups(state, { payload }) {
      return {
        ...state,
        groups: payload?.items || [],
        groupsPagination: {
          ...state.groupsPagination,
          ...toPaginate(payload),
        },
      };
    },

    queryEntities(state, { payload }) {
      return {
        ...state,
        data: payload?.items || [],
        pagination: {
          ...state.pagination,
          ...toPaginate(payload),
        },
      };
    },

    queryPremiumEntities(state, { payload }) {
      return {
        ...state,
        premium: payload?.items || [],
        premiumPagination: {
          ...state.premiumPagination,
          ...toPaginate(payload),
        },
      };
    },

    queryFavorites(state, { payload }) {
      return {
        ...state,
        favorites: payload?.items || [],
        favoritesPagination: {
          ...state.favoritesPagination,
          ...toPaginate(payload),
        },
      };
    },

    // Optimistic favorite toggle: flips `isFavorite` on the main grid
    // (`data` / `premium`) AND inserts/removes the item from the
    // `favorites` slice so the favorites tab reflects the change with
    // zero network traffic. The slice is reconciled the next time the
    // user opens the favorites tab (`onTabChange` → `fetchFavorites`).
    querySetFavorite(state, { payload }) {
      const { id, isFavorite } = payload;
      const next = !isFavorite;
      const updateItem = (item) => (item.id === id ? { ...item, isFavorite: next } : item);

      let favorites = state.favorites;
      if (next) {
        const target =
          state.data.find((it) => it.id === id) || state.premium.find((it) => it.id === id);
        if (target && !favorites.some((f) => f.id === id)) {
          favorites = [...favorites, { ...target, isFavorite: true }];
        }
      } else {
        favorites = favorites.filter((f) => f.id !== id);
      }

      return {
        ...state,
        data: state.data.map(updateItem),
        premium: state.premium.map(updateItem),
        favorites,
      };
    },

    queryDeleteItem(state, { payload }) {
      return {
        ...state,
        data: state.data.filter((item) => item.id !== payload.id),
        premium: state.premium.filter((item) => item.id !== payload.id),
      };
    },

    markFavoritesChanged(state) {
      return { ...state, favoritesChanged: true };
    },

    clearFavoritesChanged(state) {
      return { ...state, favoritesChanged: false };
    },

    queryToggleActive(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
        premium: state.premium.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        ...(_.isString(payload) && { searchCriteria: payload }),
        ...(_.isObject(payload) && {
          [`search${_.capitalize(payload.viewContext)}Criteria`]: payload.criteria,
        }),
      };
    },

    orderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: !!payload?.state,
        detail: !!payload?.detail,
        id: payload?.state ? payload?.id ?? null : null,
        item: payload?.state ? payload?.item ?? null : null,
        success: false,
      };
    },

    queryItem(state, { payload }) {
      return { ...state, item: payload };
    },

    success(state, { payload }) {
      const next = typeof payload === 'boolean' ? payload : !!payload?.success;
      return { ...state, success: next };
    },

    queryCleanup() {
      return initialState;
    },
  },
};

export default Model;
